import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Sidebar from './Sidebar'; 
import Topbar from './Topbar';
import PackGrid from './PackGrid';
import PreviewModal from './PreviewModal';
import MobileNav from './MobileNav';
import SoundsView from './admin/SoundsView';
import SFXView from './SFXView';
import OverlaysView from './OverlaysView';
import CourseView from './CourseView';
import PluginDownloadPage from './PluginDownloadPage';
import AIScriptMatcher from './AIScriptMatcher';
import { api } from "../services/api";
import { Loader2 } from 'lucide-react';

const CACHE_KEY = 'packs_cache_v1';
const CACHE_TTL = 5 * 60 * 1000;

const getCachedPacks = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
};

const setCachedPacks = (data) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() }));
  } catch {
    // localStorage cheio ou indisponível — ignora silenciosamente
  }
};

const DashboardWithBackend = ({ onLogout, user }) => {
  const [activeView, setActiveView] = useState('home');
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(!getCachedPacks());
  const [previewPack, setPreviewPack] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [orientationFilter, setOrientationFilter] = useState('vertical');
  const [hasSFXAccess, setHasSFXAccess] = useState(false);
  const [hasSFXPlan, setHasSFXPlan] = useState(false);
  const [sfxDaysRemaining, setSfxDaysRemaining] = useState(0);
  const [sessionExpiredMsg, setSessionExpiredMsg] = useState('');

  const isLocked = false;

  // Escuta evento global de sessão expirada disparado pelo fetchAPI
  useEffect(() => {
    const handleAuthExpired = (e) => {
      setSessionExpiredMsg(e.detail || 'Sessão expirada. Faça login novamente.');
      setTimeout(() => {
        onLogout();
      }, 2500);
    };
    window.addEventListener('auth:expired', handleAuthExpired);
    return () => window.removeEventListener('auth:expired', handleAuthExpired);
  }, [onLogout]);

  // ============================================
  // CARREGAMENTO DE DADOS
  // ============================================
  const loadData = useCallback(async () => {
    try {
      // Se há cache, exibe imediatamente sem spinner
      const cached = getCachedPacks();
      if (cached) {
        setPacks(cached);
      } else {
        setLoading(true);
      }

      // Sempre busca favoritos e acesso SFX frescos (dados de usuário)
      const [packsData, favoritesData] = await Promise.all([
        cached ? Promise.resolve(null) : api.getPacks(),
        api.getFavorites().catch(() => ({ favorites: [] }))
      ]);

      if (packsData) {
        const list = Array.isArray(packsData) ? packsData : [];
        setPacks(list);
        setCachedPacks(list);
      }

      setFavorites(favoritesData?.favorites || []);

      // Verifica acesso aos SFX
      try {
        const sfxAccessData = await api.checkSFXAccess();
        setHasSFXAccess(sfxAccessData.has_access);
        setHasSFXPlan(sfxAccessData.has_plan || sfxAccessData.has_access);
        setSfxDaysRemaining(sfxAccessData.days_remaining || 0);
      } catch (err) {
        setHasSFXAccess(false);
        setHasSFXPlan(false);
      }

    } catch (err) {
      console.error('Erro ao carregar biblioteca:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ============================================
  // FILTRO BLINDADO
  // ============================================
  const filteredPacks = useMemo(() => {
    if (!Array.isArray(packs)) return [];
    let filtered = [...packs];

    if (activeView === 'references') {
      return filtered.filter(p => p.storage_account === 'C');
    }

    if (activeView === 'favorites') {
      // Mostrar apenas favoritos
      return filtered.filter(p => favorites.includes(p.id));
    }

    filtered = filtered.filter(p => p.storage_account !== 'C');
    
    // Filtro de orientação
    if (orientationFilter && orientationFilter !== 'all') {
      filtered = filtered.filter(p => p.orientation === orientationFilter);
    }

    if (activeView === 'downloads') {
      filtered = filtered.filter(p => p.download_count > 0);
    }

    if (selectedCollection) {
      filtered = filtered.filter(p => p.collection === selectedCollection);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(p => p.title?.toLowerCase().includes(q));
    }

    // Adicionar flag isFavorite a cada pack
    return filtered.map(p => ({
      ...p,
      isFavorite: favorites.includes(p.id)
    }));
  }, [packs, activeView, selectedCollection, searchQuery, favorites, orientationFilter]);

  // ============================================
  // LISTA DE COLEÇÕES
  // ============================================
  const collectionsList = useMemo(() => {
    const counts = packs.reduce((acc, p) => {
      if (p.storage_account !== 'C' && p.collection) {
        acc[p.collection] = (acc[p.collection] || 0) + 1;
      }
      return acc;
    }, {});
    return Object.entries(counts).map(([name, count]) => ({ name, count }));
  }, [packs]);

  // ============================================
  // TOGGLE FAVORITO
  // ============================================
  const handleToggleFavorite = async (packId) => {
    try {
      const isFavorited = favorites.includes(packId);
      
      if (isFavorited) {
        // Remover favorito
        await api.removeFavorite(packId);
        setFavorites(prev => prev.filter(id => id !== packId));
      } else {
        // Adicionar favorito
        await api.addFavorite(packId);
        setFavorites(prev => [...prev, packId]);
      }
    } catch (error) {
      console.error('Erro ao alternar favorito:', error);
      // Recarregar favoritos em caso de erro
      try {
        const favoritesData = await api.getFavorites();
        setFavorites(favoritesData?.favorites || []);
      } catch (e) {
        console.error('Erro ao recarregar favoritos:', e);
      }
    }
  };

  // ============================================
  // DOWNLOAD DE PACK
  // ============================================
  const handleDownload = async (pack) => {
    try {
      // Abre preview do pack
      setPreviewPack(pack);
    } catch (error) {
      console.error('Erro ao abrir pack:', error);
    }
  };

  // ============================================
  // LOADING
  // ============================================
  if (loading) {
    return (
      <div className="min-h-screen bg-[#05050A] flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-[#0056D2]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#05050A] text-white font-['Helvetica',_Arial,_sans-serif]">

      {/* Banner de sessão expirada */}
      {sessionExpiredMsg && (
        <div className="fixed top-0 left-0 right-0 z-[9999] bg-red-600 text-white text-center py-3 px-4 text-sm font-bold shadow-lg">
          🔐 {sessionExpiredMsg} Redirecionando para login...
        </div>
      )}

      <Sidebar
        user={user}
        collections={collectionsList}
        packs={packs}
        favorites={favorites}
        activeView={activeView}
        setActiveView={setActiveView}
        selectedCollection={selectedCollection}
        setSelectedCollection={setSelectedCollection}
        onLogout={onLogout}
        isOpen={isSidebarOpen}
        setIsOpen={setIsSidebarOpen}
        hasSFXAccess={hasSFXAccess}
        hasSFXPlan={hasSFXPlan}
        sfxDaysRemaining={sfxDaysRemaining}
      />

      <main className="md:ml-[260px] min-h-screen transition-all">
        <Topbar 
          user={user}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery} 
          onOpenMenu={() => setIsSidebarOpen(true)}
          orientationFilter={orientationFilter}
          setOrientationFilter={setOrientationFilter}
          activeView={activeView}
        />

        <div className="px-4 md:px-8 py-6">

          <h1 className={`text-3xl font-bold mb-8 tracking-tight uppercase ${activeView === 'course' ? 'hidden' : ''}`}>
            {activeView === 'references'
              ? 'Referências Premium'
              : activeView === 'sounds'
                ? 'Músicas Virais'
                : activeView === 'sfx'
                  ? 'Efeitos Sonoros (SFX)'
                  : activeView === 'plugin'
                    ? 'Plugin Editor Premium'
                    : activeView === 'course'
                      ? 'Plugin de Legendas'
                      : activeView === 'favorites'
                        ? '⭐ Meus Favoritos'
                        : activeView === 'ai-matcher'
                          ? '🤖 IA Matching'
                          : (selectedCollection || 'Biblioteca')}
          </h1>


          {/* =========================
              SWITCH DE VIEWS
          ========================= */}

          {activeView === 'ai-matcher' ? (

            <AIScriptMatcher 
              user={user}
              onDownload={handleDownload}
            />

          ) : activeView === 'plugin' ? (

            <PluginDownloadPage user={user} />

          ) : activeView === 'course' ? (

            <CourseView />

          ) : activeView === 'sounds' ? (

            <SoundsView />

          ) : activeView === 'sfx' ? (

            <SFXView />

          ) : activeView === 'overlays' ? (

            <OverlaysView />

          ) : (

            <PackGrid
              packs={filteredPacks}
              desktopCols={orientationFilter === 'horizontal' ? 3 : 5}
              onPreview={setPreviewPack}
              onToggleFavorite={handleToggleFavorite}
            />

          )}
        </div>
      </main>

      <MobileNav
        activeTab={activeView}
        setActiveTab={setActiveView}
      />

      {previewPack && (
        <PreviewModal 
          pack={previewPack}
          isOpen={true}
          onClose={() => setPreviewPack(null)}
          onToggleFavorite={handleToggleFavorite}
        />
      )}
    </div>
  );
};

export default DashboardWithBackend;
